// src/pages/yachts/special-offers/validate.js
// Mirror server validation rules for inline UX. The backend is authoritative.

import moment from 'moment';

export const OFFER_TYPES = [
    'percentage',
    'flat_amount',
    'free_days',
    'tiered_discount',
    'package'
];

export const AUDIENCES = ['public', 'authenticated', 'broker_restricted'];

// Rate currencies are stored lower-case and compared verbatim, so an offer priced in
// anything else can never match a rate. Mirrors `util/currency.signs` on the API.
export const CURRENCIES = [
    'usd',
    'eur',
    'aud',
    'gbp',
    'aed',
    'cad',
    'chf',
    'hkd',
    'nzd',
    'sek'
];

// Human-readable labels for the DB enum values.
export const OFFER_TYPE_LABELS = {
    percentage: 'Percentage',
    flat_amount: 'Flat Amount',
    free_days: 'Free Days',
    tiered_discount: 'Tiered Discount',
    package: 'Package'
};

export const AUDIENCE_LABELS = {
    public: 'Public',
    authenticated: 'Authenticated',
    broker_restricted: 'Broker Restricted'
};

export const offerTypeLabel = (value) => OFFER_TYPE_LABELS[value] || value;
export const audienceLabel = (value) => AUDIENCE_LABELS[value] || value;

const isBlank = (v) => v === undefined || v === null || v === '';

export default function validate(values, props = {}) {
    const errors = {};
    if (!values.name) errors.name = 'Required';
    else if (String(values.name).length > 255) errors.name = 'Must be 255 characters or fewer';
    if (!values.offer_type) errors.offer_type = 'Required';
    if (!values.date_from) errors.date_from = 'Required';
    if (!values.date_to) errors.date_to = 'Required';
    if (values.date_from && values.date_to && values.date_from >= values.date_to) {
        errors.date_to = 'Must be after date from';
    }
    // Existing offers stay editable once their season has passed.
    if (!props.isEdit && values.date_to && moment(values.date_to).isBefore(moment(), 'day')) {
        errors.date_to = 'Charter window has already closed — this offer could never be booked';
    }
    if (!values.publish_start_date) errors.publish_start_date = 'Required';
    if (!values.publish_end_date) errors.publish_end_date = 'Required';
    if (
        values.publish_start_date &&
        values.publish_end_date &&
        !moment(values.publish_start_date).isBefore(moment(values.publish_end_date))
    ) {
        errors.publish_end_date = 'Must be after publish start';
    }

    // Publishing may start mid-window (late availability), but never after the last
    // chartered day — the offer could never be booked.
    if (values.publish_start_date && values.date_to) {
        if (moment(values.publish_start_date).isAfter(moment(values.date_to), 'day')) {
            errors.publish_start_date =
                'Cannot start after the charter window closes — the offer could never be booked';
        }
    }

    if (
        values.booking_window_start &&
        values.booking_window_end &&
        values.booking_window_start > values.booking_window_end
    ) {
        errors.booking_window_end = 'Must be after booking window start';
    }
    if (
        values.booking_window_end &&
        values.publish_start_date &&
        moment(values.booking_window_end).isBefore(moment(values.publish_start_date), 'day')
    ) {
        errors.booking_window_end =
            'Booking deadline would pass before the offer becomes visible';
    }
    if (
        values.booking_window_start &&
        values.publish_end_date &&
        moment(values.booking_window_start).isAfter(moment(values.publish_end_date), 'day')
    ) {
        errors.booking_window_start =
            'Offer would stop being visible before bookings open';
    }

    const minDays = isBlank(values.min_charter_days) ? null : Number(values.min_charter_days);
    const maxDays = isBlank(values.max_charter_days) ? null : Number(values.max_charter_days);
    if (minDays !== null && (!Number.isInteger(minDays) || minDays < 1)) {
        errors.min_charter_days = 'Must be a positive integer';
    }
    if (maxDays !== null && (!Number.isInteger(maxDays) || maxDays < 1)) {
        errors.max_charter_days = 'Must be a positive integer';
    }
    if (minDays && maxDays && minDays > maxDays) {
        errors.max_charter_days = 'Must be at least the minimum charter days';
    }
    if (minDays && values.date_from && values.date_to && !values.allow_partial_application) {
        const windowDays = moment(values.date_to).diff(moment(values.date_from), 'days');
        if (minDays > windowDays) {
            errors.min_charter_days = `Longer than the ${windowDays}-day charter window — no charter could qualify`;
        }
    }

    if (values.offer_type === 'percentage') {
        const dp = Number(values.discount_percent);
        if (!(dp > 0 && dp <= 100)) {
            errors.discount_percent = 'Must be between 0 and 100';
        }
    }
    if (values.offer_type === 'flat_amount') {
        if (!(Number(values.discount_amount) > 0)) {
            errors.discount_amount = 'Required, > 0';
        }
        if (!values.discount_amount_currency) {
            errors.discount_amount_currency = 'Required';
        } else if (!CURRENCIES.includes(String(values.discount_amount_currency).toLowerCase())) {
            errors.discount_amount_currency = 'Not a currency any rate uses';
        }
    }
    if (values.offer_type === 'free_days') {
        const fd = Number(values.free_days);
        if (!Number.isInteger(fd) || fd <= 0) {
            errors.free_days = 'Must be a positive integer';
        } else if (minDays === null) {
            // Pricing bills `charter_days - free_days`; without a floor a short charter
            // would come out at zero.
            errors.min_charter_days = 'Required for free-days offers';
        } else if (fd >= minDays) {
            errors.free_days = `Must be fewer than the ${minDays}-day minimum, or the charter prices at zero`;
        }
    }
    if (
        values.offer_type === 'tiered_discount' ||
        values.offer_type === 'package'
    ) {
        const tiers = values.tiers || [];
        if (!tiers.length) errors.tiers = { _error: 'At least one tier required' };
        else {
            const tierErrors = tiers.map((t) => {
                const e = {};
                const min = isBlank(t.min_duration_days) ? null : Number(t.min_duration_days);
                const max = isBlank(t.max_duration_days) ? null : Number(t.max_duration_days);
                if (min === null) e.min_duration_days = 'Required';
                else if (!Number.isInteger(min) || min < 1) {
                    e.min_duration_days = 'Must be a positive integer';
                }
                if (max !== null && (!Number.isInteger(max) || max < 1)) {
                    e.max_duration_days = 'Must be a positive integer';
                } else if (min !== null && max !== null && max < min) {
                    e.max_duration_days = 'Must be at least the min days';
                }
                if (values.offer_type === 'tiered_discount') {
                    const dp = Number(t.discount_percent);
                    if (!(dp > 0 && dp <= 100)) {
                        e.discount_percent = '0 < x ≤ 100';
                    }
                }
                if (values.offer_type === 'package') {
                    if (!(Number(t.package_price) > 0)) {
                        e.package_price = 'Required, > 0';
                    }
                    if (!t.package_currency) {
                        e.package_currency = 'Required';
                    } else if (!CURRENCIES.includes(String(t.package_currency).toLowerCase())) {
                        e.package_currency = 'Not a currency any rate uses';
                    }
                }
                return e;
            });

            // The applied tier is the first array match, so overlapping bands would
            // resolve by row order rather than by the better discount.
            const bands = tiers
                .map((t, i) => ({
                    i,
                    min: isBlank(t.min_duration_days) ? null : Number(t.min_duration_days),
                    max: isBlank(t.max_duration_days) ? null : Number(t.max_duration_days)
                }))
                .filter((b) => b.min !== null && !tierErrors[b.i].min_duration_days)
                .sort((a, b) => a.min - b.min);
            for (let n = 1; n < bands.length; n += 1) {
                const prev = bands[n - 1];
                if (prev.max === null || bands[n].min <= prev.max) {
                    tierErrors[bands[n].i].min_duration_days = 'Overlaps another tier';
                }
            }

            if (tierErrors.some((e) => Object.keys(e).length)) {
                errors.tiers = tierErrors;
            }
        }
    }
    // Tiers left over from another offer type are stripped on submit (see
    // `buildOfferPayload`), not surfaced as an error the user cannot clear.

    if (values.audience === 'broker_restricted') {
        if (
            !Array.isArray(values.allowed_broker_ids) ||
            values.allowed_broker_ids.length === 0
        ) {
            errors.allowed_broker_ids = 'At least one broker required';
        }
    }

    if (values.access_code && !String(values.access_code).trim()) {
        errors.access_code = 'Cannot be whitespace';
    }

    return errors;
}
